/**
 * Shared Views Query Fetchers
 *
 * React Query hooks and fetchers for shared views API.
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import {
	keepPreviousData,
	queryOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { normalizeVisibilityForUrl } from "@/lib/utils/property-visibility";
import { fetchPublicJson } from "@/lib/utils/public-api";
import { queryFetcher } from "@/queries/config/query-fetcher";
import { sharedViewQueryKeys } from "@/queries/query-keys/shared-views";
import type { Property, PropertyGroup } from "@/queries/workspace/fetchers";
import type { PropertyVisibilityFilter } from "@/types/property";
import type {
	CreateSharedViewRequest,
	PublicSharedViewData,
	SharedViewWithDetails,
	UpdateSharedViewRequest,
} from "@/types/shared-view";

// ============================================================================
// Query Options
// ============================================================================

/**
 * Get shared views for a workspace or view
 */
export const getSharedViewsQueryOptions = (filters: {
	enabled?: boolean;
	workspaceId?: string;
	viewId?: string;
}) => {
	const params = new URLSearchParams();
	if (filters.workspaceId) params.set("workspaceId", filters.workspaceId);
	if (filters.viewId) params.set("viewId", filters.viewId);

	return queryOptions({
		enabled:
			Boolean(filters.workspaceId || filters.viewId) &&
			(filters.enabled ?? true),
		placeholderData: keepPreviousData,
		queryFn: () =>
			queryFetcher<SharedViewWithDetails[]>(
				`/api/shared-views?${params.toString()}`,
				{ method: "GET" },
			),
		queryKey: sharedViewQueryKeys.list(filters),
		staleTime: 30 * 1000, // 30 seconds
	});
};

/**
 * Get a specific shared view by ID
 */
export const getSharedViewQueryOptions = (id: string) => {
	return queryOptions({
		enabled: Boolean(id),
		queryFn: () =>
			queryFetcher<SharedViewWithDetails & { publicUrl: string }>(
				`/api/shared-views/${id}`,
				{ method: "GET" },
			),
		queryKey: sharedViewQueryKeys.detail(id),
		staleTime: 30 * 1000, // 30 seconds
	});
};

export async function getPublicSharedView(slug: string) {
	return fetchPublicJson<PublicSharedViewData>(
		`/api/shared-views/public/${slug}`,
		{ method: "GET" },
	);
}

/**
 * Get public shared view data (PUBLIC / INTERNAL / RESTRICTED via req cookies)
 */
export function getPublicSharedViewQueryOptions(slug: string) {
	return queryOptions({
		enabled: Boolean(slug),
		queryFn: () => getPublicSharedView(slug),
		queryKey: sharedViewQueryKeys.publicBySlug(slug),
		retry: false,
		staleTime: 60 * 1000,
	});
}

export async function getPublicSharedViewWorkspaceProperties({
	email,
	shouldFilterByPropertyGroupIds = false,
	slug,
	sessionToken,
	visibility,
}: {
	slug: string;
	email?: string;
	shouldFilterByPropertyGroupIds?: boolean;
	/** Session token for RESTRICTED shared views (third-party embed contexts) */
	sessionToken?: string;
	visibility?: PropertyVisibilityFilter;
}): Promise<Property[]> {
	const params = new URLSearchParams();
	if (shouldFilterByPropertyGroupIds) {
		params.set("shouldFilterByPropertyGroupIds", "true");
	}
	const normalizedVisibility = normalizeVisibilityForUrl(visibility);
	if (normalizedVisibility) {
		params.set("visibility", normalizedVisibility);
	}

	return fetchPublicJson<Property[]>(
		`/api/shared-views/public/${slug}/workspace/properties${params.size ? `?${params.toString()}` : ""}`,
		{ email, method: "GET", sessionToken },
	);
}

export const getPublicSharedViewWorkspacePropertiesQueryOptions = ({
	email,
	shouldFilterByPropertyGroupIds = false,
	slug,
	sessionToken,
	visibility,
}: {
	slug: string;
	email?: string;
	shouldFilterByPropertyGroupIds?: boolean;
	/** Session token for RESTRICTED shared views (third-party embed contexts) */
	sessionToken?: string;
	visibility?: PropertyVisibilityFilter;
}) => {
	const normalizedVisibility = normalizeVisibilityForUrl(visibility);
	return queryOptions({
		enabled: Boolean(slug),
		placeholderData: keepPreviousData,
		queryFn: () =>
			getPublicSharedViewWorkspaceProperties({
				email,
				sessionToken,
				shouldFilterByPropertyGroupIds,
				slug,
				visibility,
			}),
		queryKey: sharedViewQueryKeys.publicWorkspaceProperties({
			email: email ?? null,
			sessionToken: sessionToken ?? null,
			shouldFilterByPropertyGroupIds,
			slug,
			visibility: normalizedVisibility,
		}),
		retry: false,
		staleTime: 60 * 1000,
	});
};

export async function getPublicSharedViewWorkspacePropertyGroups({
	email,
	shouldFilterByPropertyGroupIds = false,
	slug,
	sessionToken,
	visibility,
}: {
	slug: string;
	email?: string;
	shouldFilterByPropertyGroupIds?: boolean;
	/** Session token for RESTRICTED shared views (third-party embed contexts) */
	sessionToken?: string;
	visibility?: PropertyVisibilityFilter;
}): Promise<PropertyGroup[]> {
	const params = new URLSearchParams();
	if (shouldFilterByPropertyGroupIds) {
		params.set("shouldFilterByPropertyGroupIds", "true");
	}
	const normalizedVisibility = normalizeVisibilityForUrl(visibility);
	if (normalizedVisibility) {
		params.set("visibility", normalizedVisibility);
	}

	return fetchPublicJson<PropertyGroup[]>(
		`/api/shared-views/public/${slug}/workspace/property-groups${params.size ? `?${params.toString()}` : ""}`,
		{ email, method: "GET", sessionToken },
	);
}

export const getPublicSharedViewWorkspacePropertyGroupsQueryOptions = ({
	email,
	shouldFilterByPropertyGroupIds = false,
	slug,
	sessionToken,
	visibility,
}: {
	slug: string;
	email?: string;
	shouldFilterByPropertyGroupIds?: boolean;
	/** Session token for RESTRICTED shared views (third-party embed contexts) */
	sessionToken?: string;
	visibility?: PropertyVisibilityFilter;
}) => {
	const normalizedVisibility = normalizeVisibilityForUrl(visibility);
	return queryOptions({
		enabled: Boolean(slug),
		placeholderData: keepPreviousData,
		queryFn: () =>
			getPublicSharedViewWorkspacePropertyGroups({
				email,
				sessionToken,
				shouldFilterByPropertyGroupIds,
				slug,
				visibility,
			}),
		queryKey: sharedViewQueryKeys.publicWorkspacePropertyGroups({
			email: email ?? null,
			sessionToken: sessionToken ?? null,
			shouldFilterByPropertyGroupIds,
			slug,
			visibility: normalizedVisibility,
		}),
		retry: false,
		staleTime: 60 * 1000,
	});
};

// ============================================================================
// Mutations
// ============================================================================

/**
 * Create a new shared view
 */
export function useCreateSharedView() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateSharedViewRequest) =>
			queryFetcher<SharedViewWithDetails & { publicUrl: string }>(
				"/api/shared-views",
				{
					body: JSON.stringify(data),
					method: "POST",
				},
			),
		onSuccess: async (_data, variables) => {
			// Invalidate relevant queries
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: sharedViewQueryKeys.list({
						workspaceId: variables.workspaceId,
					}),
				}),
				queryClient.invalidateQueries({
					queryKey: sharedViewQueryKeys.list({ viewId: variables.viewId }),
				}),
			]);
		},
	});
}

/**
 * Update a shared view
 */
export function useUpdateSharedView() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string;
			data: UpdateSharedViewRequest | { visualizationIds: string[] };
		}) =>
			queryFetcher<SharedViewWithDetails>(`/api/shared-views/${id}`, {
				body: JSON.stringify(data),
				method: "PATCH",
			}),
		onSuccess: async (data) => {
			// Update the detail cache
			queryClient.setQueryData(sharedViewQueryKeys.detail(data.id), data);
			// Invalidate list queries
			await queryClient.invalidateQueries({
				queryKey: sharedViewQueryKeys.lists(),
			});
		},
	});
}

/**
 * Delete/revoke a shared view
 */
export function useDeleteSharedView() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			permanent = false,
		}: {
			id: string;
			permanent?: boolean;
		}) =>
			queryFetcher<{ success: boolean }>(
				`/api/shared-views/${id}?permanent=${permanent}`,
				{
					method: "DELETE",
				},
			),
		onSuccess: async (_, variables) => {
			queryClient.removeQueries({
				queryKey: sharedViewQueryKeys.detail(variables.id),
			});
			queryClient.setQueriesData<SharedViewWithDetails[]>(
				{ queryKey: sharedViewQueryKeys.lists() },
				(previousShares) =>
					previousShares?.filter((share) => share.id !== variables.id),
			);
			await queryClient.invalidateQueries({
				queryKey: sharedViewQueryKeys.lists(),
			});
		},
	});
}

/**
 * Add access emails to a restricted shared view
 */
export function useAddAccessEmails() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, emails }: { id: string; emails: string[] }) =>
			queryFetcher<SharedViewWithDetails>(`/api/shared-views/${id}/access`, {
				body: JSON.stringify({ emails }),
				method: "POST",
			}),
		onSuccess: async (data) => {
			// Update the detail cache
			queryClient.setQueryData(sharedViewQueryKeys.detail(data.id), data);
			// Invalidate list queries so share dialog list updates immediately
			await queryClient.invalidateQueries({
				queryKey: sharedViewQueryKeys.lists(),
			});
		},
	});
}

/**
 * Remove access email from a shared view
 */
export function useRemoveAccessEmail() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, email }: { id: string; email: string }) =>
			queryFetcher<SharedViewWithDetails>(`/api/shared-views/${id}/access`, {
				body: JSON.stringify({ email }),
				method: "DELETE",
			}),
		onSuccess: async (data) => {
			// Update the detail cache
			queryClient.setQueryData(sharedViewQueryKeys.detail(data.id), data);
			// Invalidate list queries so share dialog list updates immediately
			await queryClient.invalidateQueries({
				queryKey: sharedViewQueryKeys.lists(),
			});
		},
	});
}

// ============================================================================
// Public Visualization Data
// ============================================================================

export interface PublicVisualizationData {
	id: string;
	visualizationId?: string;
	name: string;
	type: string;
	description?: string | null;
	data: unknown;
	cards?: unknown;
	metrics?: unknown;
	// Some visualizations (e.g. Lease Expirations) return a multi-source map of sync times
	// like { leaseExpiration: "...", leaseHistory: "...", rentRoll: "..." }.
	lastSyncedAt: string | null | Record<string, string | undefined>;
	filterOptions?: unknown;
	allowDrillDown: boolean;
	allowDownload: boolean;
}

/**
 * Query options for fetching public visualization data
 */
export const getPublicVisualizationQueryOptions = ({
	slug,
	visualizationName,
	viewVisualizationId,
	transformParams = {},
	viewerEmail,
	sessionToken,
}: {
	slug: string;
	visualizationName?: string;
	viewVisualizationId?: string;
	transformParams?: Record<string, unknown>;
	viewerEmail?: string;
	/** Session token for RESTRICTED shared views (third-party embed contexts) */
	sessionToken?: string;
}) => {
	return queryOptions({
		enabled: Boolean(slug && (viewVisualizationId || visualizationName)),
		queryFn: () =>
			fetchPublicJson<PublicVisualizationData>(
				`/api/shared-views/public/${slug}/visualization`,
				{
					body: JSON.stringify({
						...(viewVisualizationId ? { viewVisualizationId } : {}),
						...(visualizationName ? { visualizationName } : {}),
						transformParams,
					}),
					email: viewerEmail,
					headers: { "Content-Type": "application/json" },
					method: "POST",
					sessionToken,
				},
			),
		queryKey: sharedViewQueryKeys.publicVisualization({
			sessionToken,
			slug,
			transformParams,
			viewerEmail,
			viewVisualizationId,
			visualizationName,
		}),
		retry: false,
		staleTime: 2 * 60 * 1000, // 2 minutes
	});
};
